import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { AuthContext } from '@/context/auth-context';
import { Loading } from '@/loading';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';

/**
 * Map raw Auth0 error responses to user-friendly messages.
 */
function getFriendlyErrorMessage(
  error: string,
  description: string | null,
): string {
  const desc = description?.toLowerCase() || '';

  if (
    desc.includes('organization is invalid') ||
    desc.includes('organization')
  ) {
    return 'Single sign-on is not configured correctly for your organization. Please contact your administrator.';
  }
  if (error === 'access_denied') {
    return 'Access was denied. You may not have permission to sign in, or the request was cancelled.';
  }
  if (error === 'login_required') {
    return 'Your session has expired. Please sign in again.';
  }
  if (desc.includes('connection') && desc.includes('disabled')) {
    return 'The sign-in method for your organization is currently disabled. Please contact your administrator.';
  }
  return 'Something went wrong during sign-in. Please try again.';
}

export const Auth0Callback = () => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      console.error(
        `Auth0 callback error: ${error}`,
        errorDescription || '(no description)',
      );
      setAuthError(getFriendlyErrorMessage(error, errorDescription));
      return;
    }

    if (loading) return;

    if (user) {
      navigate('/', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [loading, user, navigate, searchParams]);

  if (authError) {
    return (
      <div className='flex justify-center items-center h-full w-full'>
        <Card>
          <CardContent className='flex flex-col gap-6'>
            <div className='flex flex-col gap-4 items-center w-80'>
              <AlertTriangle className='h-10 w-10 text-yellow-500' />
              <h3 className='text-xl font-semibold text-foreground'>
                Sign-in failed
              </h3>
              <p className='text-sm text-muted-foreground text-center'>
                {authError}
              </p>
              <Button asChild className='w-full'>
                <Link to='/login'>Back to login</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <Loading />;
};
